import lodash from 'lodash';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import { nodeWithArgs, node, toQuery, toMutation } from 'common/util/queryHelper';
import { queryGraphQL, mutateGraphQL } from 'common/util/query';
import { DOCUMENT_SOURCE_TYPE } from 'common/util/studyHelpers';

export const LOAD_STUDY_EXPECTATIONS = 'LOAD_STUDY_EXPECTATIONS';
export const LOAD_STAFF_DOCUMENT_TYPES = 'LOAD_STAFF_DOCUMENT_TYPES';
export const EVALUATE_STUDY_EXPECTATIONS = 'EVALUATE_STUDY_EXPECTATIONS';
export const EVALUATING_STUDY_EXPECTATIONS = 'EVALUATING_STUDY_EXPECTATIONS';
export const PROCESSING_STUDY_EXPECTATIONS = 'PROCESSING_STUDY_EXPECTATION';
export const PROCESSING_STAFF_DOCUMENT_TYPES = 'PROCESSING_STAFF_DOCUMENT_TYPES';
export const UNLOAD_UPLOADED_DOCUMENTS = 'UNLOAD_UPLOADED_DOCUMENTS';
export const UNLOAD_STUDY_EXPECTATIONS = 'UNLOAD_STUDY_EXPECTATIONS';
export const ADD_UPLOADED_DOCUMENTS_TO_STUDY_EXPECTATIONS = 'ADD_UPLOADED_DOCUMENTS_TO_STUDY_EXPECTATIONS';

export function addStudyExpectations(docTypesWithRoles) {
    return async (dispatch, getState) => {
        dispatch(processingStudyExpectations(true));
        const storeState = getState();
        const study = lodash.get(storeState, 'study');
        const input = getStudyExpectationsToAdd(docTypesWithRoles, study);

        if(lodash.isEmpty(input)) {
            dispatch(evaluateStudyExpectations());
            dispatch(processingStudyExpectations(false));
            return;
        }
        const studyExpectationsMutation = nodeWithArgs('createStudyExpectations', {
            institutionId: study.institutionId,
            studyId: study.id,
            input,
        }, [
            'name',
            'studyId',
            'studyStageId',
            'sourceType',
            'sourceId',
            'institutionId',
            'studyRoleId'
        ]);

        const response = await mutateGraphQL(toMutation(studyExpectationsMutation));
        if (response && response.errors) {
            dispatch(addApplicationMessage('error', 'There is an error adding study expectations'));
        } else {
            dispatch(addApplicationMessage('success', `${docTypesWithRoles.length} expectation(s) added.`));
            dispatch(evaluateStudyExpectations());
        }
        dispatch(processingStudyExpectations(false));
    };
}

export function removeStudyExpectations(studyExpectationIds) {
    if (lodash.isEmpty(studyExpectationIds)) {
        return;
    }
    return async (dispatch, getState) => {
        dispatch(processingStudyExpectations(true));
        const storeState = getState();
        const study = lodash.get(storeState, 'study');
        const studyExpectationMutation = nodeWithArgs('deleteStudyExpectation', {
            studyId: study.id,
            institutionId: study.institutionId,
            studyExpectationIds: studyExpectationIds,
        });

        const response = await mutateGraphQL(toMutation(studyExpectationMutation));
        if (response && response.error) {
            dispatch(addApplicationMessage('error', 'There is an error deleting study expectations'));
        } else {
            dispatch(addApplicationMessage('success', `${studyExpectationIds.length} study expectation(s) removed.`));
        }
        dispatch(loadStudyExpectations());
        return dispatch(processingStudyExpectations(false));
    };
}

export function loadStudyExpectations() {
    return async (dispatch, getState) => {
        dispatch(processingStudyExpectations(true));

        const storeState = getState();
        const { id: studyId, currentStageId } = storeState.study;
        const studyExpectationsQuery = nodeWithArgs('getStudyExpectations', {
            studyId
        }, [
            'id',
            'name',
            'studyId',
            'studyStageId',
            'studyRoleId',
            'sourceType',
            'sourceId',
            'institutionId',
            'createdBy'
        ]);
        const response = await queryGraphQL(toQuery(studyExpectationsQuery));
        if (response && response.errors) {
            return dispatch(addApplicationMessage('error', 'There is an error loading study expectations'));
        } else {
            dispatch({
                type: LOAD_STUDY_EXPECTATIONS,
                payload: {
                    studyExpectations: response.data.getStudyExpectations,
                    currentStageId: currentStageId,
                }
            });
            dispatch(processingStudyExpectations(false));
        }
    };
}

export function findAndAssignDuplicateDocuments(studyExpectation, lastUploadedDocument) {
    return async (dispatch, getState) => {

        const storeState = getState();
        const { study : { id: studyId, binderId, institutionId } } = storeState;
        const documentIds = [lastUploadedDocument.id];

        const query = nodeWithArgs('getStudyAllDuplicateDocuments', {
            studyId,
            binderId,
            documentIds,
            institutionId,
        }, [
            'documentId',
            node('duplicates', [
                'documentId',
                'documentName',
                'documentTypeId',
            ])
        ]);

        const queryResponse = await queryGraphQL(toQuery(query));
        if (queryResponse && queryResponse.errors) {
            return dispatch(addApplicationMessage('error', 'There is an error in the application'));
        } else {
            dispatch({
                type: ADD_UPLOADED_DOCUMENTS_TO_STUDY_EXPECTATIONS,
                payload: {
                    lastUploadedDocument,
                    studyExpectation,
                    foundDuplicates: queryResponse.data.getStudyAllDuplicateDocuments,
                },
            });
        }
    };
}

export function unloadUploadedDocuments(studyExpectation) {
    return {
        type: UNLOAD_UPLOADED_DOCUMENTS,
        payload: studyExpectation,
    };
}

export function evaluateStudyExpectations() {
    return async (dispatch, getState) => {
        dispatch(evaluatingStudyExpectations(true));
        const storeState = getState();
        const { id: studyId } = storeState.study;
        const studyExpectationsQuery = nodeWithArgs('evaluateStudyExpectations', {
            studyId
        }, [
            node('studyExpectationStatuses', [
                'status',
                'passed',
                'reason',
                'studyExpectationId',
                'studyTeamMemberId',
            ]),
            node('studyExpectations', [
                'id',
                'name',
                'studyId',
                'sourceId',
                'studyRoleId',
                'studyTeamMemberId'
            ]),
            node('documents', [
                'id',
                'name',
                node('descriptors', [
                    'id',
                    'values',
                ]),
                node('documentType', [
                    'id',
                    'name',
                ])
            ]),
        ]);
        const response = await queryGraphQL(toQuery(studyExpectationsQuery));
        if (response && response.errors) {
            dispatch(evaluatingStudyExpectations(false));
            // return dispatch(addApplicationMessage('error', 'There is an error loading study expectations'));
        } else {
            dispatch({
                type: EVALUATE_STUDY_EXPECTATIONS,
                payload: response.data.evaluateStudyExpectations
            });
            dispatch(evaluatingStudyExpectations(false));
        }
    };
}

export function loadStaffDocumentTypes() {
    return async (dispatch, getState) => {
        dispatch(processingStaffDocumentTypes(true));

        const { study: { studyType, id: studyId } } = getState();

        const staffDocumentQuery = nodeWithArgs('getStudyStaffDocumentTypes', {
            studyId,
            studyTypeId: studyType.id,
            institutionId: studyType.institutionId,
            binderTemplateId: studyType.binderTemplateId,
        }, [
            'id',
        ]);

        const response = await queryGraphQL(toQuery(staffDocumentQuery));

        if (response && response.errors) {
            return dispatch(addApplicationMessage('error', 'There is an error loading staff document types'));
        } else {
            dispatch({
                type: LOAD_STAFF_DOCUMENT_TYPES,
                payload: response.data.getStudyStaffDocumentTypes,
            });
        }

        dispatch(processingStaffDocumentTypes(false));
    };
}

export function processingStudyExpectations(status) {
    return {
        type: PROCESSING_STUDY_EXPECTATIONS,
        payload: status
    };
}

export function processingStaffDocumentTypes(status) {
    return {
        type: PROCESSING_STAFF_DOCUMENT_TYPES,
        payload: status
    };
}

export function evaluatingStudyExpectations(status) {
    return {
        type: EVALUATING_STUDY_EXPECTATIONS,
        payload: status
    };
}

function getStudyExpectationsToAdd(docTypesWithRoles, study) {
    return docTypesWithRoles.flatMap(
        docType => {
            if (docType.roles && docType.roles.length > 0) {
                return docType.roles.map(
                    role => mapToStudyExpectationWithRole(docType, role, study)
                );
            }
            return mapToStudyExpectation(docType, study);
        }
    );
}

// note : removed studyStageId, adding it back in future feature work.
function mapToStudyExpectation(docType, study) {
    const { id, institutionId } = study;

    return {
        name: docType.displayName,
        studyId: id,
        sourceId: docType.id,
        institutionId: institutionId,
        sourceType: DOCUMENT_SOURCE_TYPE,
    };
}

function mapToStudyExpectationWithRole(docType, role, study) {
    return {
        ...mapToStudyExpectation(docType, study),
        studyRoleId: role.id
    };
}

export function unloadStudyExpectations() {
    return {
        type: UNLOAD_STUDY_EXPECTATIONS,
    };
}
